import { useState } from 'react';
import * as yup from "yup";
import { Formik } from "formik";
import { Col, FloatingLabel, Form, Row, Container } from 'react-bootstrap';
import { ProviderServiceModel } from '../../data/entities';
import { sharedProperty } from '../../hooks';
import { Button, FormSectionDivider, FormSectionHeader, FormToggleLabel, HandleDropDownListChange, SearchableServiceDropdown } from '../../components';

interface ProviderServicesAddPageProps {
  showState: sharedProperty<boolean>;
  providerId: number;
  onSubmit: any;
}

export function ProviderServicesAddPage(props: Readonly<ProviderServicesAddPageProps>) {
  const [overrideDuration, setOverrideDuration] = useState<boolean>(false);

  const validationSchema = yup.object().shape({
    serviceId: yup.number().required("Service is required")
      .test("is-valid-service", "Please select a service", (value) => value > 0),
    providerId: yup.number().required("Provider is required"),
    duration: yup.number().when('overrideDuration', {
      is: true,
      then: (schema) => schema.required("Duration is required"),
      otherwise: (schema) => schema.nullable(),
    }),
    bufferDuration: yup.number().when('overrideDuration', {
      is: true,
      then: (schema) => schema.required("Buffer duration is required"),
      otherwise: (schema) => schema.nullable(),
    })
  });

  return (
    <Formik
        validationSchema={validationSchema}
        initialValues={{
          serviceId: 0,
          providerId: props.providerId,
          duration: overrideDuration ? null : 60,
          bufferDuration: overrideDuration ? null : 15,
          overrideDuration: overrideDuration,
        }}
        onSubmit={async (values) => {
          if (!overrideDuration) {
            values.duration = null;
            values.bufferDuration = null;
          }
          props.onSubmit({
            ...values,
            id: 0
          } as ProviderServiceModel);
          props.showState.setter(false);
        }}
      >
        {({values, handleChange, touched, errors, setFieldValue, handleSubmit }) => {
          return (
            <Form onSubmit={handleSubmit} className="h-100 d-flex flex-column justify-content-between py-3">
              <Container className="px-3">
                {/* Service */}
                <FormSectionHeader>Service</FormSectionHeader>
                <Row>
                  <Col>
                    <Form.Group controlId="serviceId">
                        <SearchableServiceDropdown
                          name="serviceId"
                          value={values.serviceId}
                          placeholder="Service"
                          onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                          isInvalid={touched.serviceId && !!errors.serviceId}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.serviceId}
                        </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <FormSectionDivider />

                {/* Duration */}
                <FormSectionHeader>Duration</FormSectionHeader>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <FormToggleLabel
                    className="me-3"
                    title="Override Service Duration"
                    description="Use a custom duration for this provider instead of the service default"
                  />
                  <div className="form-check form-switch fs-4 flex-shrink-0 ps-0">
                    <input
                      className="form-check-input ms-0 mt-0"
                      type="checkbox"
                      role="switch"
                      style={{ cursor: "pointer" }}
                      checked={overrideDuration}
                      onChange={() => {
                        const newValue = !overrideDuration;
                        setOverrideDuration(newValue);
                        setFieldValue('overrideDuration', newValue);
                        if (!newValue) {
                          setFieldValue('duration', values.duration);
                          setFieldValue('bufferDuration', values.bufferDuration);
                        }
                      }}
                    />
                  </div>
                </div>

                {
                  overrideDuration &&
                  <Row className="mt-3">
                    <Col xs={24} md={12}>
                      <Form.Group controlId="duration">
                        <FloatingLabel label="Duration (min)" className="mb-3">
                          <Form.Control
                            type="number"
                            name="duration"
                            placeholder="Duration"
                            value={values.duration!}
                            onChange={handleChange}
                            isInvalid={touched.duration && !!errors.duration}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.duration}
                          </Form.Control.Feedback>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Group controlId="bufferDuration">
                        <FloatingLabel label="Buffer Duration (min)" className="mb-3">
                          <Form.Control
                            type="number"
                            name="bufferDuration"
                            placeholder="Buffer Duration"
                            value={values.bufferDuration!}
                            onChange={handleChange}
                            isInvalid={touched.bufferDuration && !!errors.bufferDuration}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.bufferDuration}
                          </Form.Control.Feedback>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                }
              </Container>

              <Container className="pt-3 border-top">
                <Row className="g-0">
                  <div className="col-12 pe-2">
                    <Button onClick={() => { props.showState.setter(false) }} variant="cancel" className="w-100">
                      Cancel
                    </Button>
                  </div>
                  <div className="col-12 ps-2">
                    <Button type='submit' variant="primary" className="w-100">
                      Save
                    </Button>
                  </div>
                </Row>
              </Container>
            </Form>
          )
        }}
      </Formik>
  )
}

export default ProviderServicesAddPage;
